import React, { useState, useEffect, useCallback } from "react";
import {
  Row,
  Col,
  Button,
  Card,
  Segmented,
  Progress,
  Space,
  Typography,
  Tag,
  Tooltip,
  Input,
  Checkbox,
} from "antd";
import { EditOutlined, DeleteOutlined, EyeOutlined } from "@ant-design/icons";
import {
  SettingOutlined,
  PlusOutlined,
  VideoCameraOutlined,
  StarOutlined,
  StarFilled,
  AudioOutlined,
  LineChartOutlined,
  LikeOutlined,
  PlayCircleOutlined,
  LockOutlined,
  MailOutlined,
  KeyOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import { Dropdown, Menu, Switch } from "antd";
import {
  PhysicianPageWrapper,
  CustomButton,
  CustomSecondaryButton,
  CustomTitle,
  CustomSubTitle,
  StyledMenu,
} from "../physician_page/styled";
import AddLibraryManagerModal from "./AddLibraryManagerModal";
import { StyledTable } from "../admin/videos/styled";

import { libraryManagerService } from "../../../services/user";
import DeleteConfirmationModal from "../physician_page/component/DeleteConfirmationModal";
import { notify, debounce } from "../../../utils/helper";
import PhysicianDetailModal from "../physician_page/component/PhysicianDetailModal";
import { capitalizeFirstLetter } from "../../../utils/utils";
import { extractToken } from "../../../utils/localStorage";
import moment from "moment";
import { CustomSearchContainer } from "../../table-item";
import Filter from "../../table-item/filter/Filter";
import { FiSearch } from "react-icons/fi";
import { globalRoles } from "../../../utils/roles";
import authService from "../../../services/auth";

const LibraryManagerPage = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [libraryManagers, setLibraryManagers] = useState([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [libraryDataById, setLibraryDataById] = useState([]);
  const [isShowDetail, setIsShowDetail] = useState(false);
  const [librarySelectId, setLibrarySelectId] = useState(null);
  const [libraryDataEdit, setLibraryDataEdit] = useState(false);
  const [idForUpdate, setIdForUpdate] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 25,
    total: 0,
  });
  const [count, setCount] = useState(0);
  const [searchText, setSearchText] = useState("");
  // const [show, setShow] = useState(false);
  // const [filterTab, setFilterTab] = useState([]);
  const [sortingColumn, setSortingColumn] = useState("");
  const [selectedLibraryManagerIds, setSelectedLibraryManagerIds] = useState([]);
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [isMfaEnabled, setIsMfaEnabled] = useState(false);

  const user = extractToken();

  const getStatusTag = (status) => {
    return (
      <Tag color={status ? "green" : "red"}>
        {status ? "Active" : "Inactive"}
      </Tag>
    );
  };

  const fetchLibraryManagers = async (searchInput=searchText) => {
    try {
      setLoading(true);
      const response = await libraryManagerService.getAll(
        pagination?.pageSize,
        pagination?.current,
        searchInput,
        sortingColumn,
      );
      // console.log("response", response?.data?.results);
      const processedData = processData(response?.data?.results || []);
      setLibraryManagers(processedData);
      setCount(response?.data?.totalResults);
    } catch (error) {
      console.error("Error fetching library managers:", error);
    } finally {
      setLoading(false);
    }
  };

  const processData = (data) => {
    return data.map((item) => ({
      ...item,
      name: capitalizeFirstLetter(item.name),
      statusValue: item.status, // Keep original boolean
      key: item.id,
    }));
  };

  useEffect(() => {
    fetchLibraryManagers();
  }, [pagination, sortingColumn]);

  const handleEdit = (record) => {
    // console.log("Edit library manager:", record);
    // Implement edit functionality
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      const res = await libraryManagerService.delete(selectedId);
      fetchLibraryManagers(); // Refresh the list
      setIsDeleteModalOpen(false);
      notify("success", "Library Manager Delete Successfully.");
      setLoading(false);
    } catch (error) {
      console.error("Error deleting library manager:", error);
      setLoading(false);
    }
  };

  const handleView = async () => {
    try {
      const res = await libraryManagerService.getById(librarySelectId);
      setLibraryDataById(res?.data);
    } catch (error) {
      console.error("Error deleting library manager:", error);
    }
  };

  useEffect(() => {
    if (librarySelectId) {
      handleView();
    }
  }, [librarySelectId]);

  const handleLibraryManagerCheckbox = (checked, record) => {
    if (checked) {
      setSelectedLibraryManagerIds([...selectedLibraryManagerIds, record.id]);
      setIsDropdownVisible(true);
    } else {
      const newSelection = selectedLibraryManagerIds.filter(id => id !== record.id);
      setSelectedLibraryManagerIds(newSelection);
      setIsDropdownVisible(newSelection.length > 0);
    }
  };

  const handleAction = async (actionType) => {
    if (selectedLibraryManagerIds.length === 0) return;
    try {
      switch (actionType) {
        case "toggleMfa":
          await authService.disableMfaUser({
            userIds: selectedLibraryManagerIds,
            is2FaDisable: !isMfaEnabled,
          });
          notify(
            "success",
            isMfaEnabled ? "2FA enabled successfully" : "2FA disabled successfully"
          );
          fetchLibraryManagers();
          break;
        case "resendInvite":
          await authService.resendInviteEmail({ userIds: selectedLibraryManagerIds });
          notify("success", "Invite emails sent successfully");
          break;
        case "resetPassword":
          await authService.resetPasswordUsers({ userIds: selectedLibraryManagerIds });
          notify("success", "Password reset emails sent successfully");
          break;
      }
      setSelectedLibraryManagerIds([]);
    } catch (error) {
      notify("error", "Failed to perform action");
    }
  };

  const actionMenu = (
    <StyledMenu>
      <Menu.Item key="mfa">
        <div className="menu-item-content">
          <LockOutlined className="menu-item-icon" />
          <span className="menu-item-text">2FA Disable</span>
          <Switch
            size="small"
            checked={isMfaEnabled}
            onChange={(checked) => {
              setIsMfaEnabled(checked);
              handleAction("toggleMfa");
            }}
          />
        </div>
      </Menu.Item>
      <Menu.Item key="resendInvite" onClick={() => handleAction("resendInvite")}>
        <div className="menu-item-content">
          <MailOutlined className="menu-item-icon" />
          <span className="menu-item-text">Resend Invite</span>
        </div>
      </Menu.Item>
      <Menu.Item key="resetPassword" onClick={() => handleAction("resetPassword")}>
        <div className="menu-item-content">
          <KeyOutlined className="menu-item-icon" />
          <span className="menu-item-text">Reset Password</span>
        </div>
      </Menu.Item>
    </StyledMenu>
    );

  const columns = [
    {
      title: "Select",
      key: "select",
      width: 60,
      fixed: "left",
      render: (_, record) => (
        <Checkbox
          checked={selectedLibraryManagerIds.includes(record.id)}
          onChange={(e) => handleLibraryManagerCheckbox(e.target.checked, record)}
        />
      ),
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "fullName",
      sorter: (a, b) => (a.fullName || "").localeCompare(b.fullName || ""),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Email Verified",
      dataIndex: "isEmailVerified",
      align: "center",
      key: "isEmailVerifiedText",
      sorter: (a,b) => (a.isEmailVerifiedText || "").localeCompare(b.isEmailVerifiedText || ""),
      // filters: [
      //   { text: "Verified", value: true },
      //   { text: "Not Verified", value: false },
      // ],
      // onFilter: (value, record) => record.isEmailVerified === value,
      render: (verified) => (
        <Tag color={verified ? "green" : "orange"}>
          {verified ? "Verified" : "Not Verified"}
        </Tag>
      ),
    },
    {
      title: "2FA Status",
      dataIndex: "is2FaDisable",
      key: "towfaStatus",
      width: 120,
      align: "center",
      sorter: (a,b) => (a.towfaStatus || "").localeCompare(b.towfaStatus || ""),
      render: (is2FaDisable) => (
        <Tag color={is2FaDisable ? "red" : "green"}>
          {is2FaDisable ? "Disabled" : "Enabled"}
        </Tag>
      ),
    },
    // {
    //   title: "Status",
    //   dataIndex: "status",
    //   align: "center",
    //   key: "status",
    //   filters: [
    //     { text: "Active", value: true },
    //     { text: "Inactive", value: false },
    //   ],
    //   onFilter: (value, record) => record.status === value,
    //   render: (status) => getStatusTag(status),
    // },
    {
      title: "Created At",
      dataIndex: "createdAt",
      align: "center",
      width: 200,
      key: "createdAt",
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
      render: (date) =>
        date ? moment(date).format("DD MMM YYYY hh:mm A") : "N/A",
    },
    {
      title: "Actions",
      align: "center",
      key: "actions",
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="View Details">
            {/* <Button type="text" icon={<EyeOutlined />} /> */}
            <div
              onClick={() => {
                setLibrarySelectId(record?.id);
                setIsShowDetail(true);
              }}
              className="cursor icon-button-view"
            >
              <EyeOutlined />
            </div>
          </Tooltip>
          {user?.role === globalRoles.SUPERADMIN && (
            <>
              <Tooltip title="Edit">
                {/* <Button type="text" icon={<EditOutlined />} /> */}
                <div
                  onClick={() => {
                    setIdForUpdate(record?.id);
                    setLibraryDataEdit(true);
                    setIsOpen(true);
                  }}
                  className="cursor icon-button-edit"
                >
                  <EditOutlined />
                </div>
              </Tooltip>
              <Tooltip title="Delete">
                {/* <Button type="text" danger icon={<DeleteOutlined />} /> */}
                <div
                  onClick={() => {
                    setSelectedId(record?.id);
                    setIsDeleteModalOpen(true);
                  }}
                  className="cursor icon-button-delete"
                >
                  <DeleteOutlined />
                </div>
              </Tooltip>
            </>
          )}
        </Space>
      ),
    },
  ];

  const handlePagination = (page) => {
    setPagination((prev) => ({ ...prev, current: page }));
  };

  const handleSearch = useCallback(
    debounce((value) => {
      fetchLibraryManagers(value, true);
    }, 500),
    []
  );

  const handleChangeTable = (a, b, c) => {
   const sortColumn = c?.column?.dataIndex;
   const sortOrder = c?.order;

  if (sortColumn && sortOrder) {
    const normalizedOrder = sortOrder === "ascend" ? "asc" : "desc";
    setSortingColumn(`${sortColumn}:${normalizedOrder}`);
  } else {
    setSortingColumn("");
  }
  };

  const hasData = libraryManagers.length > 0;
  const scroll = hasData ? { x: "max-content" } : null;

  // Add this function to check selected users' 2FA status
  const checkSelectedUsers2FAStatus = () => {
    if (selectedLibraryManagerIds.length === 0) return false;
    return libraryManagers.some(
      (user) => selectedLibraryManagerIds.includes(user.id) && user.is2FaDisable
    );
  };

  // Add this useEffect to update isMfaEnabled when selection changes
  useEffect(() => {
    setIsMfaEnabled(checkSelectedUsers2FAStatus());
  }, [selectedLibraryManagerIds, libraryManagers]);

  return (
    <>
      {isOpen && (
        <AddLibraryManagerModal
          isOpen={isOpen}
          setOpen={setIsOpen}
          isEdit={libraryDataEdit}
          setIsEdit={setLibraryDataEdit}
          libraryDataId={idForUpdate}
          fetchLibraryManager={fetchLibraryManagers}
        />
      )}

      {isDeleteModalOpen && (
        <DeleteConfirmationModal
          isOpen={isDeleteModalOpen}
          setOpen={setIsDeleteModalOpen}
          loading={loading}
          onConfiem={() => handleDelete()}
        />
      )}

      {isShowDetail && (
        <PhysicianDetailModal
          isOpen={isShowDetail}
          setOpen={setIsShowDetail}
          title={"Library Manager Details"}
          subtitle={"View the details of the library manager."}
          DataById={libraryDataById}
        />
      )}

      <PhysicianPageWrapper style={{ padding: "40px 60px" }}>
        <Row className="custom-header" style={{marginBottom: "25px"}}>
          <Col className="header-item">
            <CustomTitle>Library Manager</CustomTitle>
            <CustomSubTitle>
              Manage your videos, record voice narrations, and send educational
              content to patients.
            </CustomSubTitle>
          </Col>
          <Col className="custom-btn">
            <CustomButton onClick={() => setIsOpen(true)}>
              <PlusOutlined className="font-18 mt-5" />
              Add Library Manager
            </CustomButton>
            {/* <CustomSecondaryButton>
              <SettingOutlined className="font-18" />
            </CustomSecondaryButton> */}
          </Col>
        </Row>

        <Row
          className="d-flex w-100"
          style={{ gap: "20px", justifyContent: "space-evenly" }}
        >
          <Col span={24}>
            <Row gutter={[16, 16]}>
              <Col lg={6} md={12} sm={24} xs={24}>
                <Card className="custom-cards">
                  <Col
                    className="d-flex mb-5"
                    style={{ justifyContent: "space-between" }}
                  >
                    <div className="font-16 font-semibold text-primary">
                      Total Videos
                    </div>
                    <div>
                      <VideoCameraOutlined className="font-19 text-secondry" />
                    </div>
                  </Col>
                  <Col className="font-27 font-bold mb-5 text-primary">142</Col>
                  <Col className="font-15 text-secondry">
                    +6 videos added this month
                  </Col>
                </Card>
              </Col>
              <Col lg={6} md={12} sm={24} xs={24}>
                <Card className="custom-cards">
                  <Col
                    className="d-flex mb-5"
                    style={{ justifyContent: "space-between" }}
                  >
                    <div className="font-16 font-semibold text-primary">
                      Voice Recordings
                    </div>
                    <div>
                      <AudioOutlined className="font-19 text-secondry" />
                    </div>
                  </Col>
                  <Col className="font-27 font-bold mb-5 text-primary">8</Col>
                  <Col className="font-15 text-secondry">
                    3 pending voice cloning
                  </Col>
                </Card>
              </Col>
              <Col lg={6} md={12} sm={24} xs={24}>
                <Card className="custom-cards">
                  <Col
                    className="d-flex mb-5"
                    style={{ justifyContent: "space-between" }}
                  >
                    <div className="font-16 font-semibold text-primary">
                      Patient Views
                    </div>
                    <div>
                      <LineChartOutlined className="font-19 text-secondry" />
                    </div>
                  </Col>
                  <Col className="font-27 font-bold mb-5 text-primary">573</Col>
                  <Col className="font-15 text-secondry">
                    +21% from last month
                  </Col>
                </Card>
              </Col>
              <Col lg={6} md={12} sm={24} xs={24}>
                <Card className="custom-cards">
                  <Col
                    className="d-flex mb-5"
                    style={{ justifyContent: "space-between" }}
                  >
                    <div className="font-16 font-semibold text-primary">
                      Average Rating
                    </div>
                    <div>
                      <StarOutlined className="font-19 text-secondry" />
                    </div>
                  </Col>
                  <Col className="font-27 font-bold mb-5 text-primary">
                    4.7/5
                  </Col>
                  <Col className="font-15 text-secondry">
                    From 218 patient ratings
                  </Col>
                </Card>
              </Col>
            </Row>
          </Col>

          <Col
            span={24}
            className="d-flex"
            style={{ justifyContent: "space-between" }}
          >
              <div className="d-flex  gap-12" style={{ width: "100%" , justifyContent:'start'}}>
              <CustomSearchContainer>
                <FiSearch style={{position: "absolute", zIndex: 1}} className="icon ml-5" />
                <Input
                  placeholder="Search here..."
                  value={searchText}
                  onChange={(e) => {
                    setSearchText(e.target.value);
                    handleSearch(e.target.value);
                  }}
                />
              </CustomSearchContainer>
            </div>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              {selectedLibraryManagerIds.length > 0 && (
                <Dropdown
                  getPopupContainer={triggerNode => triggerNode.parentNode}
                  overlay={actionMenu}
                  trigger={["click"]}
                  placement="bottomRight"
                  open={isDropdownVisible}
                  onOpenChange={setIsDropdownVisible}
                >
                  <Button
                    className="text-primary"
                    icon={<MoreOutlined className="text-primary" />}
                  >
                    Actions ({selectedLibraryManagerIds.length})
                  </Button>
                </Dropdown>
              )}
            </div>
          </Col>

          <Col span={24} style={{ flex: 1, padding: "0px" }}>
            <StyledTable
              columns={columns}
              dataSource={libraryManagers}
              rowKey="id"
              scroll={scroll}
              pagination={{
                current: pagination?.current,
                pageSize: pagination?.pageSize,
                showSizeChanger: true,
                pageSizeOptions: [25, 50, 100],
                onShowSizeChange: (current, size) => {
                  setPagination({
                    ...pagination,
                    current,
                    pageSize: size,
                  });
                },
                total: count,
                showTotal: (total, range) =>
                  `${range[0]} - ${range[1]} of ${total} library manager`,
                showQuickJumper: false,
                showLessItems: true,
                onChange: handlePagination,
                itemRender: (page, type, originalElement) => {
                  if (type === "prev") {
                    return (
                      <Button
                        type="text"
                        disabled={pagination?.current === 1}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (pagination?.current > 1) {
                            setPagination((prev) => ({
                              ...prev,
                              current: prev.current - 1,
                            }));
                          }
                        }}
                      >
                        Previous
                      </Button>
                    );
                  }
                  if (type === "next") {
                    const maxPage = Math.ceil(count / pagination?.pageSize);
                    return (
                      <Button
                        type="text"
                        disabled={pagination?.current === maxPage}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (pagination?.current < maxPage) {
                            setPagination((prev) => ({
                              ...prev,
                              current: prev.current + 1,
                            }));
                          }
                        }}
                      >
                        Next
                      </Button>
                    );
                  }
                  return originalElement;
                },
              }}
              onChange={handleChangeTable}
            />
          </Col>
        </Row>
      </PhysicianPageWrapper>
    </>
  );
};

export default LibraryManagerPage;
